/** Credit note / refund against a Xero invoice: credits what's owing first, refunds the rest from a chosen bank account. */
import React, { useEffect, useState } from 'react';
import { Loader2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { api, gbp } from '@/pages/StaffInvoiceCentre';

export function CreditNoteDialog({ row, onClose, onDone }) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!row) return;
    setAmount(String(Number(row.amount_due > 0 ? row.amount_due : row.total).toFixed(2))); setReason('');
    api(`/staff/invoices/bank-accounts?business=${row.business}`).then((d) => { setAccounts(d.accounts || []); setAccountId(d.defaults?.bank || d.accounts?.[0]?.id || ''); }).catch(() => setAccounts([]));
  }, [row]);
  if (!row) return null;
  const amt = Number(amount) || 0;
  const allocate = Math.min(amt, row.amount_due);
  const refund = Math.max(0, amt - row.amount_due);
  const submit = async () => {
    setBusy(true);
    try {
      const d = await api(`/staff/invoices/${row.id}/credit`, { method: 'POST', body: JSON.stringify({ amount: amt, reason, refund_account_id: refund > 0 ? accountId : null }) });
      toast.success(`Credit note ${d.credit.number} raised in Xero${d.credit.refunded ? ` · ${gbp(d.credit.refunded)} refund recorded` : ''}`);
      (d.warnings || []).forEach((w) => toast.warning(w));
      onDone?.(d);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="ic-credit-dialog">
        <DialogHeader><DialogTitle>Credit note — {row.number}</DialogTitle><DialogDescription>{row.contact} · total {gbp(row.total)}, {gbp(row.amount_due)} owing. Creates an approved credit note in Xero.</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          <div><Label className="text-xs">Credit amount (£, inc VAT)</Label><Input type="number" step="0.01" min="0.01" max={row.total} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="ic-credit-amount" /></div>
          <div className="rounded-lg bg-gray-50 border p-3 text-xs space-y-1" data-testid="ic-credit-breakdown">
            <div className="flex justify-between"><span>Knocked off what's owing</span><b>{gbp(allocate)}</b></div>
            <div className={`flex justify-between ${refund > 0 ? 'text-amber-800' : 'text-gray-400'}`}><span>Refunded to the customer</span><b>{gbp(refund)}</b></div>
            {refund > 0 && (accounts.length ? <select value={accountId} onChange={(e) => setAccountId(e.target.value)} className="mt-1 w-full h-9 border rounded-md px-2 bg-white" data-testid="ic-credit-account">{accounts.map((a) => <option key={a.id} value={a.id}>Refund paid from: {a.name}{a.code ? ` (${a.code})` : ''}</option>)}</select> : <p className="text-rose-700">No bank account available for the refund.</p>)}
          </div>
          <div><Label className="text-xs">Reason (shown on the credit note)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Goods returned / overcharged / goodwill" data-testid="ic-credit-reason" /></div>
          <Button onClick={submit} disabled={busy || !(amt > 0) || amt > row.total + 0.005 || (refund > 0 && !accountId)} className="w-full bg-rose-700 hover:bg-rose-600 text-white font-bold" data-testid="ic-credit-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Undo2 className="w-4 h-4 mr-2" />} Raise {gbp(amt)} credit note in Xero</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
